import { useCallback, useState, type ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { TestMark } from '../TestMark';
import type { PendingAction } from '../problems/ProblemActionDialog';
import type { ProblemGroup } from '../problems/problem-types';
import type { FlowLinks } from './FlowDetail';
import { FlowGlyph } from './FlowGlyph';
import type { FlowIncident } from './flow-types';
import { engineeringReport, tellCustomer } from './incident-handoff';
import {
  behindWords,
  customerSees,
  handlingWords,
  incidentHeadline,
  incidentMeaning,
  incidentSize,
  ownerBadge,
  signalWords,
} from './incident-words';
import { IncidentWho, type IncidentLoaders } from './IncidentWho';
import type { ViewerAsk } from './viewer-types';

const GLYPH = { red: 'stuck', amber: 'slow', neutral: 'held' } as const;

export interface FlowIncidentCardProps {
  incident: FlowIncident;
  open: boolean;
  onToggle: () => void;
  loaders: IncidentLoaders;
  links: FlowLinks;
  stamp: string;
  logsProject: string | null;
  /** Opens a staff action's confirm step; absent for a role that may not act. */
  onAsk: ((p: PendingAction) => void) | undefined;
  /** Starts the recorded, read-only support grant into a company; absent when the app offers none. */
  onViewAsCustomer?: ((company: { id: string; name: string }, reason: string) => void) | undefined;
  /** Opens one of its files in the support viewer, through the grant; absent for a role that may not open one. */
  onSee?: ((ask: ViewerAsk) => void) | undefined;
}

/** Copies text and says so for a moment; says what went wrong when the browser refuses. */
function useCopy(): [string | null, (what: string, text: string) => void] {
  const [said, setSaid] = useState<string | null>(null);
  const copy = useCallback((what: string, text: string) => {
    const done = () => setSaid(`${what} copied`);
    const failed = () =>
      setSaid(`The browser would not copy; select the text in ${what.toLowerCase()} instead`);
    void (navigator.clipboard?.writeText(text) ?? Promise.reject(new Error('no clipboard'))).then(
      done,
      failed,
    );
    window.setTimeout(() => setSaid(null), 4000);
  }, []);
  return [said, copy];
}

function Head({
  incident: i,
  open,
  onToggle,
  f,
}: {
  incident: FlowIncident;
  open: boolean;
  onToggle: () => void;
  f: Formatter;
}): ReactNode {
  return (
    <button type="button" className="fl-inc__head" aria-expanded={open} onClick={onToggle}>
      <FlowGlyph tone={GLYPH[i.tone]} />
      <span className="fl-inc__title">
        <strong>{incidentHeadline(i)}</strong>
        <span className="fl-inc__size">{incidentSize(f, i)}</span>
      </span>
      <span className={`fl-inc__badge fl-inc__badge--${i.tone}`}>{ownerBadge[i.handling]}</span>
    </button>
  );
}

/** One fact of the incident: a label and what it says. */
function Fact({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="fl-inc__fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Facts({ incident: i, f }: { incident: FlowIncident; f: Formatter }): ReactNode {
  const sees = customerSees(i);
  return (
    <dl className="fl-inc__facts">
      <Fact label="What it means">{incidentMeaning(i)}</Fact>
      <Fact label="What the customer sees">
        {sees ? (
          <q className="fl-inc__quote">{sees}</q>
        ) : (
          'Nothing tells them anything is wrong; their documents show as in progress.'
        )}
      </Fact>
      <Fact label="What Paige does">{handlingWords(f, i)}</Fact>
      <Fact label="Behind it">{behindWords(f, i)}</Fact>
      <Fact label="Started">
        {f.ago(i.startedAt)}, oldest at {i.top.name}
        {i.top.testCompany ? <TestMark /> : null}
        {i.top.jobName ? `, ${i.top.jobName}` : ''}
      </Fact>
      {i.signals.length > 0 ? (
        <Fact label="At the same time">
          <ul className="fl-inc__signals">
            {i.signals.map((s) => (
              <li key={`${s.kind}:${s.id}`} data-signal={s.kind}>
                {signalWords(s)}
              </li>
            ))}
          </ul>
        </Fact>
      ) : null}
    </dl>
  );
}

function ForEngineering({ incident: i }: { incident: FlowIncident }): ReactNode {
  return (
    <details className="fl-inc__eng">
      <summary>For engineering</summary>
      <p>
        Cause <code>{i.id}</code> at {i.places.join(', ')}; tries {i.attempts ?? '-'} of{' '}
        {i.maxAttempts ?? '-'}.
      </p>
      <p>
        {i.detail ? <code>{i.detail}</code> : 'The last error is shown to support and engineering.'}
      </p>
    </details>
  );
}

/** The engineering report for what the panel shows: the cause, and up to three of its companies. */
function reportOf(f: Formatter, props: FlowIncidentCardProps & { groups: ProblemGroup[] }): string {
  const { incident: i, groups, links, logsProject } = props;
  return engineeringReport(f, i, {
    origin: window.location.origin,
    timelines: groups.slice(0, 3).map((g) => links.problems(g.company.slug)),
    logsProject,
    tenants: groups.length > 0 ? groups.map((g) => g.company.id) : [i.top.id],
    asOf: new Date().toISOString(),
  });
}

/** "Tell the customer", "Copy for engineering", "View as customer". */
function HandOn(props: FlowIncidentCardProps & { groups: ProblemGroup[] }): ReactNode {
  const { incident: i, groups, onViewAsCustomer } = props;
  const f = useFormat();
  const [said, copy] = useCopy();
  const company = groups[0]?.company ?? { id: i.top.id, name: i.top.name };
  const report = () => reportOf(f, props);
  return (
    <div className="fl-inc__handon">
      {i.kind === 'theirs' || i.kind === 'ours' ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => copy('The message', tellCustomer(f, i, company.name))}
        >
          Tell the customer
        </Button>
      ) : null}
      {i.kind === 'held' ? null : (
        <Button size="sm" variant="secondary" onClick={() => copy('The report', report())}>
          Copy for engineering
        </Button>
      )}
      {onViewAsCustomer ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onViewAsCustomer(company, `Stages and queues: ${incidentHeadline(i)}`)}
        >
          See {company.name}’s files
        </Button>
      ) : null}
      <span className="fl-inc__said" role="status">
        {said}
      </span>
    </div>
  );
}

/** One cause, folded across companies: what, whose, what the customer sees, what Paige does, who, and what to do. */
export function FlowIncidentCard(props: FlowIncidentCardProps): ReactNode {
  const { incident: i, open, onToggle, loaders, links, stamp, onAsk } = props;
  const f = useFormat();
  const [groups, setGroups] = useState<ProblemGroup[]>([]);
  return (
    <li className={`fl-inc fl-inc--${i.tone}`} data-incident={i.id} data-tone={i.tone}>
      <Head incident={i} open={open} onToggle={onToggle} f={f} />
      {open ? (
        <div className="fl-inc__body">
          <Facts incident={i} f={f} />
          <HandOn {...props} groups={groups} />
          <h4 className="fl-inc__h">Who and which files</h4>
          <IncidentWho
            incident={i}
            loaders={loaders}
            links={links}
            stamp={stamp}
            onAsk={onAsk}
            onGroups={setGroups}
            onSee={props.onSee}
          />
          <ForEngineering incident={i} />
        </div>
      ) : null}
    </li>
  );
}
